// Utility types for debounce and external functions
import { debounce } from '@github/mini-throttle';
import { initCollectionScripts } from '.';
import Ajaxinate from 'ajaxinate';
import { updateFiltersResetLink, updateSortResetLink } from './facets-utils';
// Feature flags for optional functionality
const FEATURES = {
    ENABLE_COUNT_CONTAINER: false,
    ENABLE_LOADING_SPINNERS: false // Set to false to disable loading spinners
};
class FacetFiltersForm extends HTMLElement {
    // Method to initialize endless collection (moved from renderProductCount)
    initEndlessCollection() {
        const productGrid = document.querySelector('#product-grid');
        const infiniteNext = document.querySelector('.infinite_next');
        if (productGrid && infiniteNext) {
            this.endlessCollection = new Ajaxinate({
                container: '#product-grid',
                pagination: '.infinite_next',
                loadingText: 'Loading...',
                callback: ()=>{
                    initCollectionScripts();
                    FacetFiltersForm.emitCollectionRendered(document.querySelector('#product-grid'));
                }
            });
        }
    }
    static setListeners() {
        const onHistoryChange = (event)=>{
            const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
            if (searchParams === FacetFiltersForm.searchParamsPrev) {
                return;
            }
            FacetFiltersForm.renderPage(searchParams, null, false);
        };
        window.addEventListener('popstate', onHistoryChange);
    }
    static toggleActiveFacets(disable = true) {
        document.querySelectorAll('.js-facet-remove').forEach((element)=>{
            element.classList.toggle('disabled', disable);
        });
    }
    static renderPage(searchParams, event, updateURLHash = true) {
        FacetFiltersForm.searchParamsPrev = searchParams;
        const sections = FacetFiltersForm.getSections();
        // Use feature flag for count container logic
        const countContainer = FEATURES.ENABLE_COUNT_CONTAINER ? document.getElementById('ProductCount') : null;
        // Only handle loading spinners if the feature is enabled
        if (FEATURES.ENABLE_LOADING_SPINNERS) {
            const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
            loadingSpinners.forEach((spinner)=>spinner.classList.remove('hidden'));
        }
        const productGridContainer = document.getElementById('ProductGridContainer');
        if (productGridContainer) {
            const collection = productGridContainer.querySelector('.collection');
            if (collection) {
                collection.classList.add('loading');
            }
        }
        if (FEATURES.ENABLE_COUNT_CONTAINER && countContainer) {
            countContainer.classList.add('loading');
        }
        sections.forEach((section)=>{
            const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
            const filterDataUrl = (element)=>element.url === url;
            if (FacetFiltersForm.filterData.some(filterDataUrl)) {
                FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
            } else {
                FacetFiltersForm.renderSectionFromFetch(url, event);
            }
        });
        if (updateURLHash) {
            FacetFiltersForm.updateURLHash(searchParams);
        }
    }
    static renderSectionFromFetch(url, event) {
        fetch(url).then((response)=>response.text()).then((responseText)=>{
            const html = responseText;
            FacetFiltersForm.filterData = [
                ...FacetFiltersForm.filterData,
                {
                    html,
                    url
                }
            ];
            // Determine the section type from the URL
            const isProductGridSection = html.includes('product-grid');
            const isFiltersSection = html.includes('main-collection-filters');
            if (isProductGridSection) {
                FacetFiltersForm.renderProductGridContainer(html);
                // Only render product count if feature flag is enabled
                if (FEATURES.ENABLE_COUNT_CONTAINER) {
                    FacetFiltersForm.renderProductCount(html);
                }
            }
            if (isFiltersSection) {
                FacetFiltersForm.renderFilters(html, event);
            }
            // Hide loading spinners if that feature is enabled
            FacetFiltersForm.hideLoadingSpinners();
            // Reinitialize endless collection
            const facetFiltersForm = document.querySelector('facet-filters-form');
            if (facetFiltersForm) {
                facetFiltersForm.initEndlessCollection();
            }
        });
    }
    static renderSectionFromCache(filterDataUrl, event) {
        const cachedSection = FacetFiltersForm.filterData.find(filterDataUrl);
        if (!cachedSection) {
            return;
        }
        const html = cachedSection.html;
        // Determine the section type from the html
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const isProductGridSection = parsedHTML.querySelector('.js-product-grid') !== null;
        const isFiltersSection = parsedHTML.querySelector('.js-main-collection-filters') !== null;
        if (isProductGridSection) {
            FacetFiltersForm.renderProductGridContainer(html);
            // Only render product count if feature flag is enabled
            if (FEATURES.ENABLE_COUNT_CONTAINER) {
                FacetFiltersForm.renderProductCount(html);
            }
        }
        if (isFiltersSection) {
            FacetFiltersForm.renderFilters(html, event);
        }
        // Hide loading spinners if that feature is enabled
        FacetFiltersForm.hideLoadingSpinners();
        // Reinitialize endless collection
        const facetFiltersForm = document.querySelector('facet-filters-form');
        if (facetFiltersForm) {
            facetFiltersForm.initEndlessCollection();
        }
    }
    // Centralized method to hide loading spinners
    static hideLoadingSpinners() {
        if (FEATURES.ENABLE_LOADING_SPINNERS) {
            const loadingSpinners = document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner');
            loadingSpinners.forEach((spinner)=>spinner.classList.add('hidden'));
        }
        // Also remove loading class from collection if it exists
        const collection = document.querySelector('#ProductGridContainer .collection');
        if (collection) {
            collection.classList.remove('loading');
        }
    }
    static emitCollectionRendered(element) {
        // Wait two RAFs so the DOM/layout has settled
        requestAnimationFrame(()=>{
            requestAnimationFrame(()=>{
                const detail = {
                    el: element || document.querySelector('#product-grid')
                };
                document.dispatchEvent(new CustomEvent('collection:rendered', {
                    detail
                }));
            });
        });
    }
    static renderProductGridContainer(html) {
        const productGridContainer = document.getElementById('ProductGridContainer');
        if (!productGridContainer) {
            return;
        }
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const parsedProductGridContainer = parsedHTML.getElementById('ProductGridContainer');
        if (parsedProductGridContainer) {
            productGridContainer.innerHTML = parsedProductGridContainer.innerHTML;
        }
        productGridContainer.querySelectorAll('.scroll-trigger').forEach((element)=>{
            element.classList.add('scroll-trigger--cancel');
        });
        initCollectionScripts();
        FacetFiltersForm.emitCollectionRendered(productGridContainer.querySelector('#product-grid'));
    }
    static renderProductCount(html) {
        // Skip if feature flag is disabled
        if (!FEATURES.ENABLE_COUNT_CONTAINER) {
            return;
        }
        const countElement = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
        // Check if count element exists in the parsed HTML
        if (!countElement) {
            return;
        }
        const container = document.getElementById('ProductCount');
        if (container) {
            container.innerHTML = countElement.innerHTML;
            container.classList.remove('loading');
        }
        // Hide loading spinners
        FacetFiltersForm.hideLoadingSpinners();
        // Reinitialize endless collection after count rendering
        const facetFiltersForm = document.querySelector('facet-filters-form');
        if (facetFiltersForm) {
            facetFiltersForm.initEndlessCollection();
        }
    }
    static renderFilters(html, event) {
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        // Update selectors to include the new .main-collection-filters section
        const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll('#main-collection-filters .js-filter, #FacetFiltersPillsForm .js-filter');
        const facetDetailsElementsFromDom = document.querySelectorAll('#main-collection-filters .js-filter, #FacetFiltersPillsForm .js-filter');
        // Remove facets that are no longer returned from the server
        Array.from(facetDetailsElementsFromDom).forEach((currentElement)=>{
            if (!Array.from(facetDetailsElementsFromFetch).some(({ id  })=>currentElement.id === id)) {
                currentElement.remove();
            }
        });
        const matchesId = (element)=>{
            const jsFilter = event ? event.target.closest('.js-filter') : undefined;
            return jsFilter ? element.id === jsFilter.id : false;
        };
        const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element)=>!matchesId(element));
        const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);
        // Find the container for rendering facets
        const facetsContainer = document.querySelector('#FacetsWrapperDesktop');
        if (!facetsContainer) {
            return;
        }
        // Iterate over facets and insert them in the correct order
        facetsToRender.forEach((elementToRender, index)=>{
            const currentElement = document.getElementById(elementToRender.id);
            if (currentElement) {
                // If the element is already in the DOM, just update its contents
                currentElement.innerHTML = elementToRender.innerHTML;
            } else {
                // Determine the correct position
                const nextElement = facetsToRender[index + 1] && document.getElementById(facetsToRender[index + 1].id);
                if (nextElement) {
                    facetsContainer.insertBefore(elementToRender, nextElement);
                } else {
                    facetsContainer.appendChild(elementToRender);
                }
            }
        });
        // Ensure active facets render correctly
        FacetFiltersForm.renderActiveFacets(parsedHTML);
        if (countsToRender && (event === null || event === void 0 ? void 0 : event.target)) {
            const closestJSFilter = event.target.closest('.js-filter');
            const closestJSFilterID = closestJSFilter === null || closestJSFilter === void 0 ? void 0 : closestJSFilter.id;
            if (closestJSFilterID) {
                FacetFiltersForm.renderCounts(countsToRender, document.getElementById(closestJSFilterID));
                const newFacetDetailsElement = document.getElementById(closestJSFilterID);
                if (newFacetDetailsElement) {
                    const newElementSelector = '.facets__summary';
                    const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);
                    const isTextInput = event.target.getAttribute('type') === 'text';
                    if (newElementToActivate && !isTextInput) {
                        newElementToActivate.focus();
                    }
                }
            }
        }
    }
    static renderActiveFacets(html) {
        const activeFacetElementSelectors = [
            '.active-facets-desktop'
        ];
        const counterSelectorId = 'js-active-filters-counter';
        let totalActiveFacets = 0;
        activeFacetElementSelectors.forEach((selector)=>{
            const activeFacetsElement = html.querySelector(selector);
            if (!activeFacetsElement) {
                return;
            }
            const domElement = document.querySelector(selector);
            if (domElement) {
                domElement.innerHTML = activeFacetsElement.innerHTML;
            }
            // Subtract 1 to account for the "Clear all" button
            totalActiveFacets = activeFacetsElement.querySelectorAll('facet-remove').length - 1;
        });
        const facetCounterElement = document.getElementById(counterSelectorId);
        if (facetCounterElement) {
            facetCounterElement.textContent = totalActiveFacets > 0 ? `(${totalActiveFacets})` : '';
        }
        FacetFiltersForm.toggleActiveFacets(false);
    }
    static renderCounts(source, target) {
        if (!source || !target) {
            return;
        }
        const targetSummary = target.querySelector('.facets__summary');
        const sourceSummary = source.querySelector('.facets__summary');
        if (sourceSummary && targetSummary) {
            targetSummary.outerHTML = sourceSummary.outerHTML;
        }
        const targetHeaderElement = target.querySelector('.facets__header');
        const sourceHeaderElement = source.querySelector('.facets__header');
        if (sourceHeaderElement && targetHeaderElement) {
            targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
        }
        const targetWrapElement = target.querySelector('.facets-wrap');
        const sourceWrapElement = source.querySelector('.facets-wrap');
        if (sourceWrapElement && targetWrapElement) {
            const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
            if (isShowingMore) {
                sourceWrapElement.querySelectorAll('.facets__item.hidden').forEach((hiddenItem)=>hiddenItem.classList.replace('hidden', 'show-more-item'));
            }
            targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
        }
    }
    static updateURLHash(searchParams) {
        window.history.pushState({
            searchParams
        }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    }
    static getSections() {
        const productGrid = document.getElementById('product-grid');
        const collectionFilters = document.getElementById('main-collection-filters');
        // Return an empty array if either section is missing
        if (!productGrid || !productGrid.dataset.id || !collectionFilters || !collectionFilters.dataset.id) {
            return [];
        }
        return [
            {
                section: productGrid.dataset.id
            },
            {
                section: collectionFilters.dataset.id
            }
        ];
    }
    createSearchParams(form) {
        const formData = new FormData(form);
        return new URLSearchParams(formData).toString();
    }
    onSubmitForm(searchParams, event) {
        FacetFiltersForm.renderPage(searchParams, event);
        this.scrollToTop();
    }
    onSubmitHandler(event) {
        event.preventDefault();
        const sortFilterForms = document.querySelectorAll('facet-filters-form form');
        const forms = [];
        sortFilterForms.forEach((form)=>{
            if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm') {
                forms.push(this.createSearchParams(form));
            }
        });
        const searchParams = forms.join('&');
        this.onSubmitForm(searchParams, event);
        // Update the reset links dynamically
        updateFiltersResetLink(searchParams);
        updateSortResetLink(searchParams);
    }
    onActiveFilterClick(event) {
        event.preventDefault();
        FacetFiltersForm.toggleActiveFacets();
        const url = event.currentTarget.href.indexOf('?') === -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
        FacetFiltersForm.renderPage(url);
    }
    constructor(){
        super();
        this.scrollToTop = ()=>{
        // Controlling this from facets-bar.ts
        };
        this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
        this.debouncedOnSubmit = debounce((event)=>{
            this.onSubmitHandler(event);
        }, 800);
        const facetForm = this.querySelector('form');
        facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
        // Initialize endless collection here (optimization)
        this.initEndlessCollection();
    }
}
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
class PriceRange extends HTMLElement {
    onRangeChange(event) {
        this.adjustToValidValues(event.currentTarget);
        this.setMinAndMaxValues();
    }
    onKeyDown(event) {
        if (event.metaKey) {
            return;
        }
        const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
        if (!event.key.match(pattern)) {
            event.preventDefault();
        }
    }
    setMinAndMaxValues() {
        const inputs = this.querySelectorAll('input');
        const minInput = inputs[0];
        const maxInput = inputs[1];
        if (maxInput.value) {
            minInput.setAttribute('data-max', maxInput.value);
        }
        if (minInput.value) {
            maxInput.setAttribute('data-min', minInput.value);
        }
        if (minInput.value === '') {
            maxInput.setAttribute('data-min', 0);
        }
        if (maxInput.value === '') {
            minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
        }
    }
    adjustToValidValues(input) {
        const value = Number(input.value);
        const min = Number(input.getAttribute('data-min'));
        const max = Number(input.getAttribute('data-max'));
        if (value < min) {
            input.value = min;
        }
        if (value > max) {
            input.value = max;
        }
    }
    constructor(){
        super();
        this.querySelectorAll('input').forEach((element)=>{
            element.addEventListener('change', this.onRangeChange.bind(this));
            element.addEventListener('keydown', this.onKeyDown.bind(this));
        });
        this.setMinAndMaxValues();
    }
}
customElements.define('price-range', PriceRange);
class FacetRemove extends HTMLElement {
    closeFilter(event) {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
    }
    constructor(){
        super();
        const facetLink = this.querySelector('a');
        facetLink.setAttribute('role', 'button');
        facetLink.addEventListener('click', this.closeFilter.bind(this));
        facetLink.addEventListener('keyup', (event)=>{
            event.preventDefault();
            if (event.code.toUpperCase() === 'SPACE') {
                this.closeFilter(event);
            }
        });
    }
}
customElements.define('facet-remove', FacetRemove);
